import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery } from 'react-query'
import { Edit3, Loader2, Search, Trash2, UserPlus, Users } from 'lucide-react'
import { useToast } from '../../../contexts/NotificationContext'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { ConfirmDialog } from '../../../componentesCompartilhados/ui/ConfirmDialog'
import { timeService } from '../../times/services/time'
import {
  faturamentoService,
  FaturamentoDto,
  AnalistaSMNDto,
  LancamentoManualPreviewDto,
  LancamentoManualTicketResumoDto,
  EtapaFaturamentoDto,
} from '../services/faturamento'
import { pessoaService } from '../../pessoas/services/pessoa'
import type { Pessoa } from '../../pessoas/tipos/pessoa'
import { InputDate } from '../../../componentesCompartilhados/form/InputDate'
import { TextoNumerico } from '../../../componentesCompartilhados/form/TextoNumerico'
import { calcularValor, minutosParaHorasDecimais } from '../../../utils/faturamentoCalculo'

type LinhaColaborador = {
  id: string
  idPessoa: number | ''
  minutos: string
}

function dataYmdParaIsoOpcional(ymd: string): string | undefined {
  const t = ymd?.trim()
  if (!t)
    return undefined
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m)
    return undefined
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0))
  return d.toISOString()
}

function formatYmdParaBr(ymd: string): string {
  const t = ymd?.trim()
  if (!t)
    return '—'
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m)
    return t
  return `${m[3]}/${m[2]}/${m[1]}`
}

/** Mesmo critério do backend (PerformanceService): descrição do time igual a "Momentum". */
function ehDescricaoTimeMomentum(descricao: string | undefined): boolean {
  return (descricao?.trim() ?? '').toLowerCase() === 'momentum'
}

/**
 * Gera id único para linhas locais. `crypto.randomUUID()` não existe fora de contexto seguro (ex.: HTTP na rede local).
 */
function gerarIdUnico(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function')
    return c.randomUUID()
  const bytes = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function')
    c.getRandomValues(bytes)
  else
    for (let i = 0; i < 16; i++)
      bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const p = (n: number) => n.toString(16).padStart(2, '0')
  let hex = ''
  for (let i = 0; i < 16; i++)
    hex += p(bytes[i])
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`
}

export default function LancamentoManual() {
  const toast = useToast()

  const [analistaSMNId, setAnalistaSMNId] = useState<number | ''>('')
  const [termoBusca, setTermoBusca] = useState('')
  const [buscaDebounced, setBuscaDebounced] = useState('')
  const [ticketSelecionado, setTicketSelecionado] = useState<LancamentoManualTicketResumoDto | null>(null)
  const [preview, setPreview] = useState<LancamentoManualPreviewDto | null>(null)
  const [codigoEtapa, setCodigoEtapa] = useState<number | ''>('')
  const [linhas, setLinhas] = useState<LinhaColaborador[]>(() => [
    { id: gerarIdUnico(), idPessoa: '', minutos: '' },
  ])
  const [dataEnvioAnalista, setDataEnvioAnalista] = useState('')
  const [dataEnvioMomentum, setDataEnvioMomentum] = useState('')
  const [dataAprovacao, setDataAprovacao] = useState('')
  const [dataFaturamento, setDataFaturamento] = useState('')
  const [numeroNf, setNumeroNf] = useState('')
  const [observacao, setObservacao] = useState('')
  const [modalColaboradorAberto, setModalColaboradorAberto] = useState(false)
  /** `__nova__` = adicionar linha ao escolher; senão `id` da linha em edição */
  const [contextoModalColaborador, setContextoModalColaborador] = useState<string | null>(null)
  const [termoBuscaModalColaborador, setTermoBuscaModalColaborador] = useState('')
  const [confirmResumoAberto, setConfirmResumoAberto] = useState(false)

  useEffect(() => {
    const h = window.setTimeout(() => setBuscaDebounced(termoBusca), 350)
    return () => window.clearTimeout(h)
  }, [termoBusca])

  const { data: analistasSMN = [] } = useQuery({
    queryKey: ['faturamento', 'analistas-smn'],
    queryFn: () => faturamentoService.obterAnalistasSMN(),
  })

  const { data: etapas = [] } = useQuery({
    queryKey: ['faturamento', 'etapas'],
    queryFn: () => faturamentoService.obterEtapasFaturamento(),
  })

  const { data: pessoas = [] } = useQuery({
    queryKey: ['pessoas', 'ativos-lancamento-manual'],
    queryFn: () => pessoaService.listarPessoas({ ativo: 'sim' }),
  })

  const { data: timesLista = [] } = useQuery({
    queryKey: ['times', 'lancamento-manual'],
    queryFn: () => timeService.listarTimes(),
    staleTime: 5 * 60 * 1000,
  })

  const idTimeMomentum = useMemo(() => {
    const t = timesLista.find((x) => ehDescricaoTimeMomentum(x.descricao))
    return t?.id
  }, [timesLista])

  /** Nunca exibir integrantes do time cuja descrição é "Momentum". */
  const pessoasElegiveis = useMemo(() => {
    return pessoas.filter((p) => {
      if (p.idPessoa == null)
        return false
      if (idTimeMomentum != null && p.timeId === idTimeMomentum)
        return false
      return true
    })
  }, [pessoas, idTimeMomentum])

  const analistaNumero = typeof analistaSMNId === 'number' ? analistaSMNId : 0

  const { data: ticketsLista = [], isFetching: carregandoTickets } = useQuery({
    queryKey: ['faturamento', 'lancamento-manual-tickets', analistaNumero, buscaDebounced],
    queryFn: () => faturamentoService.listarTicketsLancamentoManual(analistaNumero, buscaDebounced || undefined),
    enabled: analistaNumero > 0,
  })

  const carregarPreview = useCallback(
    async (t: LancamentoManualTicketResumoDto) => {
      const p = await faturamentoService.obterPreviewLancamentoManual(analistaNumero, t.tipo, t.idTicket)
      setPreview(p)
    },
    [analistaNumero]
  )

  useEffect(() => {
    if (!ticketSelecionado || analistaNumero <= 0) {
      setPreview(null)
      return
    }
    void carregarPreview(ticketSelecionado)
  }, [ticketSelecionado, analistaNumero, carregarPreview])

  const valorHoraEfetivo = preview?.valorHoraEfetivo ?? 0

  const totaisLinhas = useMemo(() => {
    let totalMin = 0
    let totalValor = 0
    for (const l of linhas) {
      const m = parseInt(l.minutos, 10)
      if (!l.idPessoa || !Number.isFinite(m) || m <= 0)
        continue
      totalMin += m
      totalValor += calcularValor(m, valorHoraEfetivo)
    }
    return { totalMin, totalValor }
  }, [linhas, valorHoraEfetivo])

  const nomePessoaPorId = useCallback(
    (id: number | '') => {
      if (id === '' || id == null)
        return ''
      const p = pessoasElegiveis.find((x) => x.idPessoa === id) ?? pessoas.find((x) => x.idPessoa === id)
      return p?.nome ?? `#${id}`
    },
    [pessoas, pessoasElegiveis]
  )

  const abrirModalColaborador = (linhaIdOuNova: string) => {
    setContextoModalColaborador(linhaIdOuNova)
    setTermoBuscaModalColaborador('')
    setModalColaboradorAberto(true)
  }

  const fecharModalColaborador = () => {
    setModalColaboradorAberto(false)
    setContextoModalColaborador(null)
    setTermoBuscaModalColaborador('')
  }

  const pessoasFiltradasModal = useMemo(() => {
    const ctx = contextoModalColaborador
    if (!ctx)
      return []

    const termo = termoBuscaModalColaborador.trim().toLowerCase()
    const linhaEditada = ctx === '__nova__' ? null : linhas.find((l) => l.id === ctx)
    const pidAtual =
      linhaEditada && typeof linhaEditada.idPessoa === 'number' ? linhaEditada.idPessoa : null

    const idsOcupadosOutras = new Set<number>()
    for (const l of linhas) {
      if (ctx !== '__nova__' && l.id === ctx)
        continue
      if (typeof l.idPessoa === 'number')
        idsOcupadosOutras.add(l.idPessoa)
    }

    return pessoasElegiveis.filter((p) => {
      const pid = p.idPessoa!
      if (idsOcupadosOutras.has(pid) && pid !== pidAtual)
        return false
      if (!termo)
        return true
      const n = (p.nome ?? '').toLowerCase()
      const e = (p.email ?? '').toLowerCase()
      return n.includes(termo) || e.includes(termo)
    })
  }, [contextoModalColaborador, linhas, pessoasElegiveis, termoBuscaModalColaborador])

  const selecionarColaboradorModal = (pessoa: Pessoa) => {
    const pid = pessoa.idPessoa
    if (pid == null)
      return
    const ctx = contextoModalColaborador
    if (!ctx)
      return

    if (ctx === '__nova__') {
      setLinhas((prev) => [...prev, { id: gerarIdUnico(), idPessoa: pid, minutos: '' }])
    }
    else {
      setLinhas((prev) =>
        prev.map((x) => (x.id === ctx ? { ...x, idPessoa: pid } : x))
      )
    }
    fecharModalColaborador()
  }

  const resumoConfirmacaoConteudo: ReactNode = useMemo(() => {
    if (!ticketSelecionado || !preview || typeof codigoEtapa !== 'number')
      return <p className="text-gray-500">—</p>

    const et = etapas.find((e) => e.codigoEtapaFaturamento === codigoEtapa)
    const linhasComValor = linhas
      .map((l) => {
        const m = parseInt(l.minutos, 10) || 0
        if (typeof l.idPessoa !== 'number' || m <= 0)
          return null
        const v = calcularValor(m, valorHoraEfetivo)
        const h = minutosParaHorasDecimais(m)
        return {
          id: l.id,
          nome: nomePessoaPorId(l.idPessoa),
          minutos: m,
          horas: h,
          valor: v,
        }
      })
      .filter((x): x is NonNullable<typeof x> => x != null)

    const totalHorasFmt = minutosParaHorasDecimais(totaisLinhas.totalMin).toFixed(2)

    const fmtMoeda = (n: number) =>
      n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

    return (
      <div className="space-y-4 max-h-[min(60vh,440px)] overflow-y-auto pr-1 text-left">
        <p className="text-sm text-gray-600">
          Confirme o lançamento manual com os dados abaixo.
        </p>

        <div className="rounded-xl border border-gray-200 bg-gray-50/90 p-4 space-y-3 shadow-theme-xs">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Ticket
            </span>
            <p className="mt-1 text-sm font-semibold leading-snug text-gray-900">{preview.nome}</p>
            <p className="mt-1.5">
              <span className="rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-gray-200">
                {preview.rotuloTipoExibicao ?? preview.tipo}
              </span>
            </p>
          </div>

          <div className="flex flex-wrap gap-6 border-t border-gray-200/80 pt-3">
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Nº ticket
              </span>
              <p className="mt-0.5 text-2xl font-bold tabular-nums text-brand-600">
                {preview.numeroTicket || preview.idTicket}
              </p>
            </div>
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Valor hora
              </span>
              <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">
                {fmtMoeda(valorHoraEfetivo)}
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200/80 pt-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Etapa
            </span>
            <p className="mt-1 text-sm font-semibold text-gray-900">
              {et?.descricao ?? '—'}{' '}
              <span className="font-normal text-gray-600">({et?.percentual ?? '—'}%)</span>
            </p>
          </div>
        </div>

        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Colaboradores
          </h3>
          <ul className="space-y-2">
            {linhasComValor.map((item) => (
              <li
                key={item.id}
                className="flex flex-col gap-0.5 rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <span className="font-semibold text-gray-900">{item.nome}</span>
                <span className="text-xs text-gray-600 sm:text-right">
                  <span className="tabular-nums">{item.minutos} min</span>
                  {' · '}
                  <span className="tabular-nums font-medium text-gray-800">{item.horas.toFixed(2)} h</span>
                  {' · '}
                  <span className="font-semibold tabular-nums text-gray-900">{fmtMoeda(item.valor)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-brand-200 bg-gradient-to-br from-brand-50 to-white p-4 shadow-theme-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-800">
                Valor total
              </span>
              <p className="mt-1 text-3xl font-bold tabular-nums text-brand-600">
                {fmtMoeda(totaisLinhas.totalValor)}
              </p>
            </div>
            <div className="text-left sm:text-right">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-800">
                Tempo total
              </span>
              <p className="mt-1 text-lg font-bold tabular-nums text-brand-900">
                {totaisLinhas.totalMin} min
                <span className="mx-1.5 font-normal text-brand-700">·</span>
                {totalHorasFmt} h
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Datas e NF
          </p>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
            <div className="flex justify-between gap-2 border-b border-gray-100/80 pb-1 sm:block sm:border-0 sm:pb-0">
              <dt className="font-medium text-gray-500">Envio analista</dt>
              <dd className="tabular-nums text-gray-900">{formatYmdParaBr(dataEnvioAnalista)}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b border-gray-100/80 pb-1 sm:block sm:border-0 sm:pb-0">
              <dt className="font-medium text-gray-500">Envio Momentum</dt>
              <dd className="tabular-nums text-gray-900">{formatYmdParaBr(dataEnvioMomentum)}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b border-gray-100/80 pb-1 sm:block sm:border-0 sm:pb-0">
              <dt className="font-medium text-gray-500">Aprovação</dt>
              <dd className="tabular-nums text-gray-900">{formatYmdParaBr(dataAprovacao)}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b border-gray-100/80 pb-1 sm:block sm:border-0 sm:pb-0">
              <dt className="font-medium text-gray-500">Faturamento</dt>
              <dd className="tabular-nums text-gray-900">{formatYmdParaBr(dataFaturamento)}</dd>
            </div>
            <div className="flex justify-between gap-2 sm:col-span-2 sm:block">
              <dt className="font-medium text-gray-500">NF</dt>
              <dd className="font-medium text-gray-900">{numeroNf.trim() || '—'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="font-medium text-gray-500">Observação</dt>
              <dd className="mt-0.5 text-gray-800 whitespace-pre-wrap break-words">
                {observacao.trim() || '—'}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    )
  }, [
    ticketSelecionado,
    preview,
    codigoEtapa,
    etapas,
    linhas,
    valorHoraEfetivo,
    nomePessoaPorId,
    totaisLinhas,
    dataEnvioAnalista,
    dataEnvioMomentum,
    dataAprovacao,
    dataFaturamento,
    numeroNf,
    observacao,
  ])

  const validarEAbrirConfirmacao = () => {
    if (typeof analistaSMNId !== 'number' || analistaSMNId <= 0) {
      toast.error('Validação', 'Selecione o analista SMN.')
      return
    }
    if (!ticketSelecionado) {
      toast.error('Validação', 'Selecione um projeto ou suporte.')
      return
    }
    if (typeof codigoEtapa !== 'number' || codigoEtapa <= 0) {
      toast.error('Validação', 'Selecione a etapa de faturamento.')
      return
    }

    const colaboradores = linhas
      .map((l) => ({
        idPessoa: typeof l.idPessoa === 'number' ? l.idPessoa : 0,
        minutos: parseInt(l.minutos, 10) || 0,
      }))
      .filter((c) => c.idPessoa > 0 && c.minutos > 0)

    if (colaboradores.length === 0) {
      toast.error('Validação', 'Informe ao menos uma linha com colaborador e minutos.')
      return
    }

    setConfirmResumoAberto(true)
  }

  const mutation = useMutation(
    async () => {
      if (typeof analistaSMNId !== 'number' || analistaSMNId <= 0)
        throw new Error('Selecione o analista SMN.')
      if (!ticketSelecionado)
        throw new Error('Selecione um projeto ou suporte.')
      if (typeof codigoEtapa !== 'number' || codigoEtapa <= 0)
        throw new Error('Selecione a etapa de faturamento.')

      const colaboradores = linhas
        .map((l) => ({
          idPessoa: typeof l.idPessoa === 'number' ? l.idPessoa : 0,
          minutos: parseInt(l.minutos, 10) || 0,
        }))
        .filter((c) => c.idPessoa > 0 && c.minutos > 0)

      if (colaboradores.length === 0)
        throw new Error('Informe ao menos uma linha com colaborador e minutos.')

      return faturamentoService.lancarManualFaturamento({
        analistaSMNId,
        tipoTicket: ticketSelecionado.tipo,
        idTicket: ticketSelecionado.idTicket,
        codigoEtapaFaturamento: codigoEtapa,
        colaboradores: colaboradores.map((c) => ({ idPessoa: c.idPessoa, minutos: c.minutos })),
        dataEnvioAnalista: dataYmdParaIsoOpcional(dataEnvioAnalista) ?? null,
        dataEnvioMomentum: dataYmdParaIsoOpcional(dataEnvioMomentum) ?? null,
        dataAprovacao: dataYmdParaIsoOpcional(dataAprovacao) ?? null,
        dataFaturamento: dataYmdParaIsoOpcional(dataFaturamento) ?? null,
        numeroNf: numeroNf.trim() || null,
        observacao: observacao.trim() || null,
      })
    },
    {
      onSuccess: (res: FaturamentoDto) => {
        setConfirmResumoAberto(false)
        toast.success('Lançamento concluído', `Faturamento #${res.idFaturamento} registrado.`)
        setTicketSelecionado(null)
        setPreview(null)
        setLinhas([{ id: gerarIdUnico(), idPessoa: '', minutos: '' }])
        setDataEnvioAnalista('')
        setDataEnvioMomentum('')
        setDataAprovacao('')
        setDataFaturamento('')
        setNumeroNf('')
        setObservacao('')
      },
      onError: (e: unknown) => {
        const msg = e instanceof Error ? e.message : 'Não foi possível concluir o lançamento.'
        toast.error('Erro', msg)
      },
    }
  )

  const onSelecionarAnalista = (pessoaId: number | null) => {
    setAnalistaSMNId(pessoaId ?? '')
    setTicketSelecionado(null)
    setPreview(null)
    setTermoBusca('')
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 sm:px-6">
      <div className="flex items-start gap-3">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Edit3 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lançamento Manual</h1>
          <p className="mt-1 text-sm text-gray-600">
            Registre faturamentos realizados fora do Zeus. Apenas administradores. Documentos de aprovação/histórico do ticket não são exigidos nesta rotina.
            Colaboradores do time Momentum não podem ser selecionados.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm space-y-4">
        <h2 className="text-sm font-semibold text-gray-800">Analista SMN</h2>
        <select
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          value={analistaSMNId === '' ? '' : String(analistaSMNId)}
          onChange={(e) => {
            const v = e.target.value
            if (!v) {
              onSelecionarAnalista(null)
              return
            }
            onSelecionarAnalista(Number(v))
          }}
        >
          <option value="">Selecione...</option>
          {analistasSMN.map((a: AnalistaSMNDto) => (
            <option key={a.pessoaId} value={a.pessoaId}>
              {a.nome}
            </option>
          ))}
        </select>
      </div>

      {analistaNumero > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm space-y-4">
          <h2 className="text-sm font-semibold text-gray-800">Buscar projeto ou suporte</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              placeholder="Nome ou número do ticket"
              className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
            />
            {carregandoTickets && <Loader2 className="h-5 w-5 animate-spin text-brand-500" />}
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
            {ticketsLista.length === 0 && !carregandoTickets && (
              <p className="p-4 text-sm text-gray-500">Nenhum ticket encontrado para este analista.</p>
            )}
            {ticketsLista.map((t: LancamentoManualTicketResumoDto) => (
              <button
                key={`${t.tipo}-${t.idTicket}`}
                type="button"
                onClick={() => setTicketSelecionado(t)}
                className={`w-full text-left px-4 py-2 text-sm border-b border-gray-100 last:border-0 hover:bg-gray-50 ${
                  ticketSelecionado?.idTicket === t.idTicket && ticketSelecionado?.tipo === t.tipo
                    ? 'bg-brand-50'
                    : ''
                }`}
              >
                <span className="font-medium text-gray-900">{t.nome}</span>
                <span className="text-gray-500"> · {t.rotuloTipoExibicao ?? t.tipo}</span>
                <span className="block text-xs text-gray-500">Ticket {t.numeroTicket || t.idTicket}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {preview && (
        <>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm space-y-2 text-sm">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Ticket selecionado</h2>
            <p>
              <span className="text-gray-500">Nome:</span> {preview.nome}
            </p>
            <p>
              <span className="text-gray-500">Ticket:</span> {preview.numeroTicket || preview.idTicket} ·{' '}
              {preview.rotuloTipoExibicao ?? preview.tipo}
            </p>
            <p>
              <span className="text-gray-500">Valor hora (efetivo):</span>{' '}
              {valorHoraEfetivo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
            <p>
              <span className="text-gray-500">Data recebimento:</span>{' '}
              {preview.dataRecebimento ? new Date(preview.dataRecebimento).toLocaleDateString('pt-BR') : '—'}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm space-y-4">
            <h2 className="text-sm font-semibold text-gray-800">Etapa de faturamento</h2>
            <select
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={codigoEtapa === '' ? '' : String(codigoEtapa)}
              onChange={(e) => setCodigoEtapa(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Selecione...</option>
              {etapas.map((et: EtapaFaturamentoDto) => (
                <option key={et.codigoEtapaFaturamento} value={et.codigoEtapaFaturamento}>
                  {et.descricao} ({et.percentual}%)
                </option>
              ))}
            </select>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-800">Colaboradores</h2>
              <button
                type="button"
                title="Adicionar colaborador"
                onClick={() => abrirModalColaborador('__nova__')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-brand-600 hover:bg-brand-50 hover:border-brand-200"
              >
                <UserPlus className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    <th className="py-2 pr-4">Colaborador</th>
                    <th className="py-2 pr-4 w-28">Minutos</th>
                    <th className="py-2 pr-4 w-24">Horas</th>
                    <th className="py-2 pr-4 w-32">Valor (R$)</th>
                    <th className="py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((linha) => {
                    const m = parseInt(linha.minutos, 10) || 0
                    const horas = m > 0 ? minutosParaHorasDecimais(m) : 0
                    const valorLinha =
                      linha.idPessoa && m > 0 ? calcularValor(m, valorHoraEfetivo) : 0
                    return (
                      <tr key={linha.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4">
                          <button
                            type="button"
                            onClick={() => abrirModalColaborador(linha.id)}
                            className="flex w-full min-w-[200px] items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-800 hover:border-brand-400 hover:bg-gray-50"
                          >
                            <span className="truncate">
                              {linha.idPessoa !== ''
                                ? nomePessoaPorId(linha.idPessoa)
                                : 'Buscar colaborador…'}
                            </span>
                            <Search className="h-4 w-4 shrink-0 text-gray-400" />
                          </button>
                        </td>
                        <td className="py-2 pr-4">
                          <TextoNumerico
                            value={linha.minutos}
                            onChange={(v) =>
                              setLinhas((prev) =>
                                prev.map((x) => (x.id === linha.id ? { ...x, minutos: v } : x))
                              )
                            }
                            placeholder="0"
                          />
                        </td>
                        <td className="py-2 pr-4 text-gray-700">
                          {linha.idPessoa && m > 0 ? horas.toFixed(2) : '—'}
                        </td>
                        <td className="py-2 pr-4 text-gray-700">
                          {linha.idPessoa && m > 0
                            ? valorLinha.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                            : '—'}
                        </td>
                        <td className="py-2">
                          {linhas.length > 1 && (
                            <button
                              type="button"
                              aria-label="Remover linha"
                              onClick={() =>
                                setLinhas((prev) => prev.filter((x) => x.id !== linha.id))
                              }
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-medium text-gray-900">
                    <td className="pt-3 text-right pr-4">Total</td>
                    <td className="pt-3 tabular-nums">
                      {totaisLinhas.totalMin > 0 ? totaisLinhas.totalMin : '—'}
                    </td>
                    <td className="pt-3 tabular-nums">
                      {totaisLinhas.totalMin > 0 ? minutosParaHorasDecimais(totaisLinhas.totalMin).toFixed(2) : '—'}
                    </td>
                    <td className="pt-3 tabular-nums">
                      {totaisLinhas.totalValor > 0
                        ? totaisLinhas.totalValor.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })
                        : '—'}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm space-y-4">
            <h2 className="text-sm font-semibold text-gray-800">Datas e nota fiscal</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1">Envio analista</span>
                <InputDate value={dataEnvioAnalista} onChange={setDataEnvioAnalista} />
              </div>
              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1">Envio Momentum</span>
                <InputDate value={dataEnvioMomentum} onChange={setDataEnvioMomentum} />
              </div>
              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1">Aprovação</span>
                <InputDate value={dataAprovacao} onChange={setDataAprovacao} />
              </div>
              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1">Faturamento</span>
                <InputDate value={dataFaturamento} onChange={setDataFaturamento} />
              </div>
            </div>
            <div>
              <span className="block text-xs font-medium text-gray-600 mb-1">Número da NF (opcional)</span>
              <input
                type="text"
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={numeroNf}
                onChange={(e) => setNumeroNf(e.target.value)}
                placeholder="Ex.: 12345"
              />
            </div>
            <div>
              <span className="block text-xs font-medium text-gray-600 mb-1">Observação (opcional)</span>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[80px]"
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Texto livre"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              disabled={mutation.isLoading}
              onClick={validarEAbrirConfirmacao}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-60"
            >
              {mutation.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmar lançamento
            </button>
          </div>

          <Modal
            isOpen={modalColaboradorAberto}
            onClose={fecharModalColaborador}
            title="Buscar colaborador"
            subtitle="Digite nome ou e-mail. Integrantes do time Momentum não são listados."
            size="lg"
            icon={
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                <Users className="h-5 w-5" />
              </div>
            }
          >
            <div className="space-y-3">
              <input
                type="search"
                autoFocus
                placeholder="Pesquisar…"
                value={termoBuscaModalColaborador}
                onChange={(e) => setTermoBuscaModalColaborador(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              />
              <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200">
                {pessoasFiltradasModal.length === 0 && (
                  <p className="p-4 text-sm text-gray-500">Nenhuma pessoa encontrada.</p>
                )}
                {pessoasFiltradasModal.map((p) => (
                  <button
                    key={p.idPessoa}
                    type="button"
                    onClick={() => selecionarColaboradorModal(p)}
                    className="flex w-full flex-col items-start border-b border-gray-100 px-4 py-2.5 text-left last:border-0 hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-900">{p.nome}</span>
                    <span className="text-xs text-gray-500">{p.email}</span>
                  </button>
                ))}
              </div>
            </div>
          </Modal>

          <ConfirmDialog
            isOpen={confirmResumoAberto}
            onClose={() => setConfirmResumoAberto(false)}
            onConfirm={() => {
              setConfirmResumoAberto(false)
              mutation.mutate()
            }}
            title="Confirmar lançamento manual"
            content={resumoConfirmacaoConteudo}
            confirmText="Confirmar lançamento"
            cancelText="Voltar"
            variant="default"
            maxWidthClassName="max-w-2xl"
          />
        </>
      )}
    </div>
  )
}
